import type { FC, ReactNode } from 'react'

import type { LibraWorkflow } from './workflowStepsApi'

/**
 * A workflow-specific context provider the generic libra shell injects only
 * for workflows that need it. Feature modules export a descriptor; the
 * registry (`workflowScopedProviders.tsx`) lists them; the shell stays
 * feature-agnostic.
 *
 * This type lives in its own file so feature modules can import it without
 * touching the registry (which imports the feature modules back -- a value
 * cycle this file exists to avoid).
 */
export interface WorkflowScopedProviderDescriptor {
  /** Stable identifier; used as the React key when composing. */
  key: string
  /**
   * Where the shell mounts the provider:
   *  - 'ticket': once per ticket page, in the LibraView stack below
   *    `TicketAccessProvider` (ticket-session lifetime).
   *  - 'workspace': wraps each `WorkspaceLayoutRenderer` mount -- below the
   *    step key and the component-tab remount boundary, so its state resets
   *    per step and per tab exactly like a locally-mounted provider would.
   */
  placement: 'ticket' | 'workspace'
  /** Pure predicate over the parsed workflow: mount this provider or not. */
  appliesTo: (workflow: LibraWorkflow) => boolean
  Provider: FC<{ children: ReactNode }>
}

/**
 * True when any step's declared `workspaceLayout` renders the given panel.
 * Data-driven off the workflow author's own configuration.
 */
export function workflowDeclaresPanel(
  workflow: LibraWorkflow,
  panelName: string
): boolean {
  return workflow.steps.some((step) =>
    step.workspaceLayout?.panels.some((panel) => panel.component === panelName)
  )
}
